import React, { useState, useEffect } from "react";

import { Grid, MenuItem, Box } from "@mui/material";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { sub } from "date-fns";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import { useActionProject } from "../../../../hooks/useActionProject";
import useActionSupplier from "../../../../hooks/useActionSupplier";
import { usePurchaseOrderContext } from "../../../../hooks/usePurchaseOrderContext";
import { useSupplierContext } from "../../../../hooks/useSupplierContext";
import useTimezoneDate from "../../../../hooks/useTimezoneDate";
import useYupValidation from "../../../../hooks/useYupValidation";
import { AutomaticSequencing } from "../../../../models/automatic.sequencing";
import { Customer } from "../../../../models/customer";
import { DifferentLocation, Location } from "../../../../models/location";
import { MilestoneType } from "../../../../models/milestone";
import { Port } from "../../../../models/port";
import { Project } from "../../../../models/project";
import { PurchaseOrder, ValueOf } from "../../../../models/purchase.order";
import { Supplier } from "../../../../models/supplier";
import CreateSupplier from "../../../../pages/private/Shared/Network/Supplier/Maintenance/Details";
import { keysToCamel } from "../../../../util/convertCamelCase";
import { OrderDetailsFields } from "../../../../util/enums";
import { formatCurrency, getPriceStringValid } from "../../../../util/prices";
import AutoSuggest from "../../../Form/AutoSuggest";
import Input from "../../../Form/Input";
import MapInput from "../../../Form/MapInput";
import Select from "../../../Form/Select";
import AddressInfoForm from "../../../Generic/AddressComponent/AddressInfoForm";
import CollapsablePanel from "../../../Generic/Collapse";
import { MilestoneDates } from "../../../Generic/MilestoneDates";
import Modal from "../../../Generic/Modal";
import ModalDelete from "../../../Generic/Modal/Delete";
import Skeleton from "../../../Generic/Skeleton";
import getOrderOptions from "../../getOrderOptions";
import { usePurchaseOrderDetails } from "../../hooks/usePurchaseOrderDetails";
import SkeletonDetails from "./skeleton";
import useStyles from "./styles";
import SupplierDetails from "./SupplierDetails";
import { SelectChangeEvent } from "@mui/material";

interface OrderDetailsProps {
  purchaseOrder?: PurchaseOrder;
  updateOrder: (attribute: keyof PurchaseOrder, value: ValueOf<PurchaseOrder> | null) => void;
  viewOnly: boolean;
  supplierView: boolean;
  supplyChainView: boolean;
  fieldHasError: (field: string) => boolean;
  getErrorByField: (field: string) => string | undefined;
  sequence?: AutomaticSequencing;
  statuses?: Option[];
  deletePurchaseOrder: (id: number) => void;
  showDeleteConfirmation: boolean;
  setShowDeleteConfirmation: (value: boolean) => void;
  portCodes?: Port[];
  loadingPorts: boolean;
}

const OrderDetails = ({
  purchaseOrder,
  updateOrder,
  viewOnly = false,
  supplierView = false,
  supplyChainView = false,
  fieldHasError,
  getErrorByField,
  sequence,
  deletePurchaseOrder,
  showDeleteConfirmation,
  setShowDeleteConfirmation,
  portCodes,
  loadingPorts,
}: OrderDetailsProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser, isSupplierPortal, isSPVersion, isFMVersion } = useAppState();
  const {
    originPortSelected,
    destinationPortSelected,
    customerSelected,
    loadingCustomers,
    customerOptions,
    fetchingMilestones,
  } = usePurchaseOrderDetails({ purchaseOrder, portCodes });

  const { responseError, milestoneDates } = usePurchaseOrderContext();
  const { getAllActiveProjects } = useActionProject();
  const {
    orderTypes,
    performancePredictionsTypes,
    incotermOptions,
    serviceTypeOptions,
    modeOptions,
  } = getOrderOptions();

  const [openModalSupplier, setOpenModalSupplier] = useState<boolean>(false);

  const { supplier, handleUpdateSupplier } = useSupplierContext();
  const { useSaveSupplier, getAllSuppliers } = useActionSupplier();
  const { mutate: saveSupplier, isLoading: savingSupplier } = useSaveSupplier((id?: number) => {
    setOpenModalSupplier(false);
    updateOrder(OrderDetailsFields.SUPPLIER, {
      ...supplier,
      id,
      addressLine1: supplier?.location?.addressLine1,
      addressLine2: supplier?.location?.addressLine2,
      suburb: supplier?.location?.suburb,
      state: supplier?.location?.state,
      country: supplier?.location?.country,
      postalCode: supplier?.location?.postalcode,
      beCode: supplier?.beCode,
    } as Supplier);
  });

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [rate, setRate] = useState("");

  const decimalCase = Number(currentUser?.currency?.decimalPoints || 2);

  const { getDateValueUtcTimezone } = useTimezoneDate();

  const formatCurrencyToInput = (value?: string | number) => {
    if (!value || Number.isNaN(Number(value)))
      return formatCurrency(0, currentUser?.currency, false);

    return formatCurrency(Number(value).toFixed(decimalCase), currentUser?.currency, false);
  };

  const onChangeRateAndMargin = () => {
    let replaced = getPriceStringValid(rate, currentUser?.currency?.decimalSeparator);
    const nValue = Number(replaced);

    if (Number.isNaN(nValue)) replaced = "0";

    updateOrder("fobPrice", Number(replaced));
  };

  const { data: supplierOptions, isFetching: loadingSupplierOptions } = getAllSuppliers();

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
    addressLine1: yup.string().required(translate("thisFieldIsRequired")),
  });

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError: fieldSupplierHasError, getErrorByField: getSupplierErrorByField } =
    useYupValidation(errors);

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          name: supplier?.name,
          addressLine1: supplier?.location?.addressLine1,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    if (errors.length !== 0) return false;

    return true;
  };

  const disableSupplierConsigneeEdit = supplyChainView && !!purchaseOrder?.id;

  const validateAndSaveSupplier = () => {
    if (!handleValidate()) return;
    if (!supplier) return;

    saveSupplier(supplier);
  };

  const handleChangeValueDate = (attribute: keyof PurchaseOrder, value: any) => {
    updateOrder(attribute, new Date(value).toISOString());
  };

  const { data: projectsOptions, isFetching: loadingProjects } = getAllActiveProjects();

  useEffect(() => {
    if (sequence && !purchaseOrder?.id)
      updateOrder(
        OrderDetailsFields.REFERENCE,
        `${sequence?.prefix ? sequence.prefix : ""}${sequence?.nextValue ? sequence.nextValue : ""}`
      );
  }, [sequence]);

  const renderProjectOptions = () => {
    if (!isFMVersion) return null;

    return (
      <>
        <Grid item xs={6}>
          <Skeleton classesName={classes.skeleton} isLoading={loadingProjects}>
            <Select
              required={isFMVersion}
              data-cy="select-project"
              name="project"
              defaultValue=""
              displayEmpty
              placeholder={translate("selectProject")}
              label={translate("project")}
              value={purchaseOrder?.projectId}
              disabled={viewOnly || supplierView}
              onChange={(event: SelectChangeEvent<any>) =>
                updateOrder(OrderDetailsFields.PROJECT_ID, event.target.value)
              }
              error={fieldHasError(OrderDetailsFields.PROJECT_ID)}
              errorMessage={getErrorByField(OrderDetailsFields.PROJECT_ID)}
            >
              {projectsOptions &&
                projectsOptions.map((projectOption: Project) => (
                  <MenuItem key={projectOption.id} value={projectOption.id}>
                    {projectOption.name}
                  </MenuItem>
                ))}
            </Select>
          </Skeleton>
        </Grid>
        <Grid item xs={6}>
          <Select
            required={isFMVersion}
            data-cy="select-performance-prediction"
            name="performance_prediction"
            label={translate(OrderDetailsFields.PERFORMANCE_PREDICTION)}
            defaultValue=""
            displayEmpty
            placeholder={translate("selectPerformancePrediction")}
            value={purchaseOrder?.performancePrediction}
            disabled={viewOnly || supplierView}
            onChange={(event: SelectChangeEvent<any>) =>
              updateOrder(OrderDetailsFields.PERFORMANCE_PREDICTION, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.PERFORMANCE_PREDICTION)}
            errorMessage={getErrorByField(OrderDetailsFields.PERFORMANCE_PREDICTION)}
          >
            {performancePredictionsTypes.map((orderType: Option) => (
              <MenuItem key={orderType.value} value={orderType.value}>
                {orderType.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={6} md={6}>
          <MapInput
            data-cy="origin-order"
            label={translate(OrderDetailsFields.ORIGIN)}
            placeholder={translate(OrderDetailsFields.ORIGIN)}
            inputValue={purchaseOrder?.origin}
            onChangeInput={(value) => updateOrder(OrderDetailsFields.ORIGIN, value)}
            showMapIcon={false}
            disabled={viewOnly || supplierView}
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <MapInput
            data-cy="destination-order"
            label={translate(OrderDetailsFields.DESTINATION)}
            placeholder={translate(OrderDetailsFields.DESTINATION)}
            inputValue={purchaseOrder?.destination}
            onChangeInput={(value) => updateOrder(OrderDetailsFields.DESTINATION, value)}
            showMapIcon={false}
            disabled={viewOnly || supplierView}
          />
        </Grid>
      </>
    );
  };

  const renderMaerskBeCode = () => {
    if (isFMVersion) return null;

    return (
      <Grid item xs={6} md={3}>
        <Input
          data-cy="input-be-code"
          name="be-code"
          label={translate("maerskBeCode")}
          placeholder={translate("maerskCode")}
          value={purchaseOrder?.supplier?.beCode}
          disabled
          onChange={(event) =>
            updateOrder(OrderDetailsFields.SUPPLIER, {
              ...purchaseOrder?.supplier,
              beCode: event.target.value,
            } as Supplier)
          }
          error={fieldHasError(OrderDetailsFields.BE_CODE)}
          errorMessage={getErrorByField(OrderDetailsFields.BE_CODE)}
        />
      </Grid>
    );
  };

  return (
    <Box
      minHeight="inherit"
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Grid container direction="row" justifyContent="flex-start" spacing={2}>
        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-reference"
            required
            name={OrderDetailsFields.REFERENCE}
            label={translate("orderNumber")}
            value={purchaseOrder?.reference}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.REFERENCE, event.target.value)}
            error={fieldHasError(OrderDetailsFields.REFERENCE)}
            errorMessage={getErrorByField(OrderDetailsFields.REFERENCE)}
            errorResponse={responseError}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Select
            required
            data-cy="select-order-type"
            name="order_type"
            label={translate(OrderDetailsFields.TYPE)}
            defaultValue=""
            displayEmpty
            placeholder={translate(OrderDetailsFields.TYPE)}
            value={purchaseOrder?.type}
            disabled={viewOnly || supplierView}
            onChange={(event: SelectChangeEvent<any>) =>
              updateOrder(OrderDetailsFields.TYPE, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.TYPE)}
            errorMessage={getErrorByField(OrderDetailsFields.TYPE)}
          >
            {orderTypes.map((orderType: Option) => (
              <MenuItem key={orderType.value} value={orderType.value}>
                {orderType.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-purchase-date"
            name="purchase_date"
            type="date"
            label={translate("dateCreated")}
            disabled={viewOnly || supplierView}
            value={getDateValueUtcTimezone(purchaseOrder?.date)}
            onChange={(event) => handleChangeValueDate(OrderDetailsFields.DATE, event.target.value)}
            error={fieldHasError(OrderDetailsFields.DATE)}
            errorMessage={getErrorByField(OrderDetailsFields.DATE)}
            required
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-booked-date"
            disabled
            name="booked_date"
            type="date"
            label={translate("bookByDate")}
            value={
              purchaseOrder?.currentEta &&
              getDateValueUtcTimezone(
                sub(new Date(purchaseOrder?.currentEta), { days: 14 }).toISOString()
              )
            }
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Skeleton isInput isLoading={loadingPorts}>
            <AutoSuggest
              data-cy="autosuggest-origin-port"
              placeholder={translate("originPort")}
              label={translate("originPort")}
              inputValue={originPortSelected?.siteCode || ""}
              options={portCodes || []}
              getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
              getOptionLabel={(port: Port) => port.siteCode || ""}
              onChange={(port: Port) => {
                if (port && port.id) {
                  updateOrder(OrderDetailsFields.ORIGIN_PORT_ID, port.id);
                  updateOrder(OrderDetailsFields.ORIGIN, port.siteCode);
                } else {
                  updateOrder(OrderDetailsFields.ORIGIN_PORT_ID, null);
                  updateOrder(OrderDetailsFields.ORIGIN, null);
                }
              }}
              error={fieldHasError("originPort")}
              errorMessage={getErrorByField("originPort")}
              disabled={viewOnly || supplierView}
              required
            />
          </Skeleton>
        </Grid>

        <Grid item xs={6} md={3}>
          <Skeleton isInput isLoading={loadingPorts}>
            <AutoSuggest
              data-cy="autosuggest-destination-port"
              placeholder={translate("destinationPort")}
              label={translate("destinationPort")}
              inputValue={destinationPortSelected?.siteCode || ""}
              options={portCodes || []}
              getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
              getOptionLabel={(port: Port) => port.siteCode || ""}
              onChange={(port: Port) => {
                if (port && port.id) {
                  updateOrder(OrderDetailsFields.DESTINATION_PORT_ID, port.id);
                  updateOrder(OrderDetailsFields.DESTINATION, port.siteCode);
                } else {
                  updateOrder(OrderDetailsFields.DESTINATION_PORT_ID, null);
                  updateOrder(OrderDetailsFields.DESTINATION, null);
                }
              }}
              error={fieldHasError("destinationPort")}
              errorMessage={getErrorByField("destinationPort")}
              disabled={viewOnly || supplierView}
              required
            />
          </Skeleton>
        </Grid>

        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-ship-via"
            name="ship-via"
            label={translate("carrier")}
            placeholder={translate("carrier")}
            value={purchaseOrder?.shipVia}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.SHIP_VIA, event.target.value)}
            error={fieldHasError(OrderDetailsFields.SHIP_VIA)}
            errorMessage={getErrorByField(OrderDetailsFields.SHIP_VIA)}
          />
        </Grid>

        {renderMaerskBeCode()}

        <Grid item xs={12} sm={6} md={3} lg={12}>
          <Input
            data-cy="input-purchasing-organisation"
            name="purchasing-organisation"
            label={translate(OrderDetailsFields.PURCHASING_ORGANISATION)}
            placeholder={translate(OrderDetailsFields.PURCHASING_ORGANISATION)}
            value={purchaseOrder?.purchasingOrganisation}
            disabled={viewOnly || supplierView}
            onChange={(event) =>
              updateOrder(OrderDetailsFields.PURCHASING_ORGANISATION, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.PURCHASING_ORGANISATION)}
            errorMessage={getErrorByField(OrderDetailsFields.PURCHASING_ORGANISATION)}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Skeleton classesName={classes.skeleton} isLoading={loadingCustomers}>
            <AutoSuggest
              data-cy="autosuggest-consignee"
              required
              name="consignee-id"
              label={translate(OrderDetailsFields.CONSIGNEE)}
              placeholder={translate("acmeClothing")}
              inputValue={customerSelected?.name || ""}
              options={customerOptions || []}
              getOptionSelected={(option?: Customer, value?: Customer) => option?.id === value?.id}
              getOptionLabel={(customer: Customer) => customer.fullName || ""}
              onChange={(customer: Customer) => {
                if (customer && customer.id) {
                  updateOrder("consigneeId", customer.id);
                } else {
                  updateOrder("consigneeId", null);
                }
              }}
              disabled={viewOnly || supplierView || disableSupplierConsigneeEdit}
              error={fieldHasError(OrderDetailsFields.CONSIGNEE)}
              errorMessage={getErrorByField(OrderDetailsFields.CONSIGNEE)}
            />
          </Skeleton>
        </Grid>

        <Grid item xs={6} md={3}>
          <Skeleton classesName={classes.skeleton} isLoading={loadingSupplierOptions}>
            <AutoSuggest
              data-cy="autosuggest-supplier"
              required
              label={translate(OrderDetailsFields.SUPPLIER)}
              placeholder={translate("searchByNameOrEmail")}
              disabled={viewOnly || supplierView || disableSupplierConsigneeEdit}
              inputValue={purchaseOrder && purchaseOrder.supplier && purchaseOrder.supplier.name}
              getOptionLabel={(supplier: Supplier) =>
                supplier && supplier?.name ? `${supplier?.name}` : ""
              }
              getOptionSelected={(option?: Supplier, value?: Supplier) => option?.id === value?.id}
              options={supplierOptions}
              onChange={(supplier: Supplier) => {
                updateOrder(OrderDetailsFields.SUPPLIER, keysToCamel(supplier));
                updateOrder(OrderDetailsFields.SUPPLIER_NAME, supplier?.name);
              }}
              error={fieldHasError(OrderDetailsFields.SUPPLIER)}
              errorMessage={getErrorByField(OrderDetailsFields.SUPPLIER)}
              addOptionTitle={translate("createNewSupplier")}
              onAddOption={() => setOpenModalSupplier(true)}
            />
          </Skeleton>
        </Grid>

        <Grid item xs={6} md={3}>
          <Select
            data-cy="select-service-type"
            name="service-type"
            defaultValue=""
            displayEmpty
            placeholder={translate(OrderDetailsFields.SERVICE_TYPE)}
            label={translate(OrderDetailsFields.SERVICE_TYPE)}
            value={purchaseOrder?.serviceType}
            disabled={viewOnly || supplierView}
            onChange={(event: SelectChangeEvent<any>) =>
              updateOrder(OrderDetailsFields.SERVICE_TYPE, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.SERVICE_TYPE)}
            errorMessage={getErrorByField(OrderDetailsFields.SERVICE_TYPE)}
          >
            {serviceTypeOptions &&
              serviceTypeOptions.map((status: Option) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>

        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-ship-to"
            name="ship-to"
            label={translate(OrderDetailsFields.SHIP_TO)}
            placeholder={translate("warehouse")}
            value={purchaseOrder?.shipTo}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.SHIP_TO, event.target.value)}
            error={fieldHasError(OrderDetailsFields.SHIP_TO)}
            errorMessage={getErrorByField(OrderDetailsFields.SHIP_TO)}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-client-code"
            name="client-code"
            label={translate(OrderDetailsFields.CLIENT_CODE)}
            placeholder={translate(OrderDetailsFields.CLIENT_CODE)}
            value={purchaseOrder?.clientCode}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.CLIENT_CODE, event.target.value)}
            error={fieldHasError(OrderDetailsFields.CLIENT_CODE)}
            errorMessage={getErrorByField(OrderDetailsFields.CLIENT_CODE)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-remark"
            name={OrderDetailsFields.REMARK}
            label={translate(OrderDetailsFields.REMARK)}
            placeholder={translate(OrderDetailsFields.REMARK)}
            value={purchaseOrder?.remark}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.REMARK, event.target.value)}
            error={fieldHasError(OrderDetailsFields.REMARK)}
            errorMessage={getErrorByField(OrderDetailsFields.REMARK)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Input
            data-cy="input-address-code"
            name="address-code"
            label={translate(OrderDetailsFields.ADDRESS_CODE)}
            placeholder="1000"
            value={purchaseOrder?.addressCode}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.ADDRESS_CODE, event.target.value)}
            error={fieldHasError(OrderDetailsFields.ADDRESS_CODE)}
            errorMessage={getErrorByField(OrderDetailsFields.ADDRESS_CODE)}
          />
        </Grid>
        <Grid item xs={6} md={isFMVersion ? 3 : 2}>
          <Input
            data-cy="input-division-code"
            name="division-code"
            label={translate(OrderDetailsFields.DIVISION_CODE)}
            placeholder={translate(OrderDetailsFields.DIVISION_CODE)}
            value={purchaseOrder?.divisionCode}
            disabled={viewOnly || supplierView}
            onChange={(event) => updateOrder(OrderDetailsFields.DIVISION_CODE, event.target.value)}
            error={fieldHasError(OrderDetailsFields.DIVISION_CODE)}
            errorMessage={getErrorByField(OrderDetailsFields.DIVISION_CODE)}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Input
            data-cy="input-division-description"
            name="division-description"
            label={translate("brand")}
            placeholder={translate(OrderDetailsFields.DIVISION_DESCRIPTION)}
            value={purchaseOrder?.divisionDescription}
            disabled={viewOnly || supplierView}
            onChange={(event) =>
              updateOrder(OrderDetailsFields.DIVISION_DESCRIPTION, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.DIVISION_DESCRIPTION)}
            errorMessage={getErrorByField(OrderDetailsFields.DIVISION_DESCRIPTION)}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Input
            data-cy="input-department-code"
            name="department-code"
            label={translate(OrderDetailsFields.DEPARTMENT_CODE)}
            placeholder={translate(OrderDetailsFields.DEPARTMENT_CODE)}
            value={purchaseOrder?.departmentCode}
            disabled={viewOnly || supplierView}
            onChange={(event) =>
              updateOrder(OrderDetailsFields.DEPARTMENT_CODE, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.DEPARTMENT_CODE)}
            errorMessage={getErrorByField(OrderDetailsFields.DEPARTMENT_CODE)}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Input
            data-cy="input-department-description"
            name="department-description"
            label={translate(OrderDetailsFields.DEPARTMENT_DESCRIPTION)}
            placeholder={translate(OrderDetailsFields.DEPARTMENT_DESCRIPTION)}
            value={purchaseOrder?.departmentDescription}
            disabled={viewOnly || supplierView}
            onChange={(event) =>
              updateOrder(OrderDetailsFields.DEPARTMENT_DESCRIPTION, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.DEPARTMENT_DESCRIPTION)}
            errorMessage={getErrorByField(OrderDetailsFields.DEPARTMENT_DESCRIPTION)}
          />
        </Grid>
        {!isSupplierPortal && (
          <Grid item xs={6} md={2}>
            <Input
              data-cy="input-fob-price"
              name="fob-price"
              label={translate("fobPrice")}
              value={isFocused ? rate : formatCurrencyToInput(purchaseOrder?.fobPrice)}
              disabled={viewOnly || supplierView}
              onFocus={() => {
                setRate(String(formatCurrencyToInput(purchaseOrder?.fobPrice)));
                setIsFocused(true);
              }}
              onChange={(e) => {
                setRate(e.target.value);
              }}
              onBlur={() => {
                onChangeRateAndMargin();
                setRate("");
                setIsFocused(false);
              }}
              placeholder={"0.00"}
              rightIcon={currentUser?.company?.currency?.code}
              inputTextAlign={"start"}
            />
          </Grid>
        )}

        <Grid item xs={6} md={2}>
          <Select
            data-cy="select-incoterm"
            name={OrderDetailsFields.INCOTERM}
            defaultValue=""
            displayEmpty
            placeholder={translate(OrderDetailsFields.INCOTERM)}
            label={translate(OrderDetailsFields.INCOTERM)}
            value={purchaseOrder?.incoterm}
            disabled={viewOnly || supplierView}
            onChange={(event: SelectChangeEvent<any>) =>
              updateOrder(OrderDetailsFields.INCOTERM, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.INCOTERM)}
            errorMessage={getErrorByField(OrderDetailsFields.INCOTERM)}
          >
            {incotermOptions &&
              incotermOptions.map((status: Option) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <Grid item xs={6} md={isSPVersion ? 3 : 2}>
          <Select
            data-cy="select-mode"
            name={OrderDetailsFields.MODE}
            defaultValue=""
            displayEmpty
            placeholder={translate(OrderDetailsFields.MODE)}
            label={translate(OrderDetailsFields.MODE)}
            value={purchaseOrder?.mode}
            disabled={viewOnly || supplierView}
            onChange={(event: SelectChangeEvent<any>) =>
              updateOrder(OrderDetailsFields.MODE, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.MODE)}
            errorMessage={getErrorByField(OrderDetailsFields.MODE)}
            required
          >
            {modeOptions &&
              modeOptions.map((status: Option) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>

        <Grid item xs={6} md={isFMVersion ? 6 : 3}>
          <Input
            data-cy="input-ship-date"
            name="ship-date"
            type="date"
            label={translate("shipDate")}
            disabled={viewOnly || supplierView}
            value={getDateValueUtcTimezone(purchaseOrder?.currentEta)}
            onChange={(event) =>
              handleChangeValueDate(OrderDetailsFields.CURRENT_ETA, event.target.value)
            }
            error={fieldHasError(OrderDetailsFields.CURRENT_ETA)}
            errorMessage={getErrorByField(OrderDetailsFields.CURRENT_ETA)}
            required
          />
        </Grid>

        <Grid item xs={6} md={isFMVersion || isSPVersion ? 6 : 5}>
          <AddressInfoForm
            addressesArray={[(purchaseOrder?.delivery || []) as DifferentLocation]}
            view={viewOnly || supplierView}
            isExpandedAddress={true}
            condition=""
            update={(value) => updateOrder(OrderDetailsFields.DELIVERY, value[0] as Location)}
            hasToShowOnlyOne={true}
            title={translate(OrderDetailsFields.DELIVERY)}
          />
        </Grid>

        {renderProjectOptions()}

        {purchaseOrder && purchaseOrder.supplier && (
          <Grid item xs={12}>
            <CollapsablePanel title={translate("supplierDetails")}>
              <SupplierDetails supplier={purchaseOrder?.supplier} />
            </CollapsablePanel>
          </Grid>
        )}

        <Grid item xs={12}>
          <div className={classes.line}></div>
        </Grid>

        <Grid item xs={12}>
          <MilestoneDates
            type={MilestoneType.ESTIMATED}
            title={translate(`estimatedDates`)}
            options={milestoneDates}
            values={purchaseOrder?.milestones || []}
            onUpdate={updateOrder}
            viewOnly={viewOnly || supplierView}
            loading={fetchingMilestones}
            fieldSize={3}
          />
        </Grid>

        {/* <Grid item xs={12}>
          <MilestoneDates
            type={MilestoneType.ACTUAL}
            title={translate(`actualDates`)}
            options={milestoneDates}
            values={purchaseOrder?.milestones || []}
            onUpdate={updateOrder}
            viewOnly={viewOnly || supplierView}
            loading={fetchingMilestones}
            fieldSize={3}
          />
        </Grid> */}
      </Grid>

      <Modal
        data-cy="create-supplier-modal"
        title={translate("newSupplier")}
        open={openModalSupplier}
        handleClose={() => setOpenModalSupplier(false)}
        showActionButtons
        actionButtonProps={{
          textSave: translate("create"),
          onSave: () => validateAndSaveSupplier(),
          onCancel: () => setOpenModalSupplier(false),
          cancelDisabled: savingSupplier,
          loading: savingSupplier,
        }}
      >
        <CreateSupplier
          supplier={supplier}
          updateSupplier={handleUpdateSupplier}
          saveSupplier={() => {}}
          viewOnly={viewOnly || supplierView}
          goBack={() => {}}
          fieldHasError={fieldSupplierHasError}
          getErrorByField={getSupplierErrorByField}
          isUpdatingAction={false}
          showActionButtons={false}
        />
      </Modal>
      <ModalDelete
        name={translate("order")}
        open={showDeleteConfirmation}
        handleClose={(isSaved) => {
          if (isSaved) deletePurchaseOrder(purchaseOrder?.id ?? 0);
          setShowDeleteConfirmation(false);
        }}
      />
    </Box>
  );
};

export { SkeletonDetails };
export default OrderDetails;
